import { Link } from 'react-router-dom'
import { Building2, GraduationCap, KeyRound, Mail, Phone, ShieldCheck } from 'lucide-react'

import { useAuth } from '@/auth/AuthContext'
import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/auth/permissions'
import { PageHeader } from '@/components/layout/AppLayout'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader } from '@/components/ui/Card'
import { formatDateTime, podsumujOddzialy } from '@/lib/format'

export function SettingsPage() {
  const { user } = useAuth()
  if (!user) return null

  return (
    <>
      <PageHeader
        title="Ustawienia"
        description="Dane Twojego konta i zakres uprawnień w systemie."
        breadcrumb={['System', 'Ustawienia']}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Twoje konto" />
          <div className="space-y-6 p-6">
            <div className="flex items-center gap-4">
              <Avatar name={user.full_name} size="lg" />
              <div>
                <p className="font-headline text-xl font-bold text-secondary">{user.full_name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <Badge tone="info">{ROLE_LABELS[user.role]}</Badge>
                  {user.must_change_password && <Badge tone="warning">Wymagana zmiana hasła</Badge>}
                </div>
              </div>
            </div>

            <dl className="grid gap-4 sm:grid-cols-2">
              <DetailItem
                icon={<Mail size={16} />}
                label="Adres e-mail (login)"
                value={user.email}
              />
              <DetailItem icon={<Phone size={16} />} label="Telefon" value={user.phone ?? '—'} />
              <DetailItem
                icon={<ShieldCheck size={16} />}
                label="Ostatnie logowanie"
                value={formatDateTime(user.last_login_at)}
              />
              <DetailItem
                icon={<Building2 size={16} />}
                label="Dostęp do oddziałów"
                value={podsumujOddzialy(user.locations, user.has_all_locations)}
              />
            </dl>

            <p className="rounded-lg bg-surface-container-low p-4 text-sm text-on-surface-variant">
              Dane osobowe i przypisanie do oddziałów zmienia administrator. Jeśli coś się nie
              zgadza, skontaktuj się z nim.
            </p>
          </div>
        </Card>

        <Card>
          <CardHeader title="Bezpieczeństwo" />
          <div className="space-y-4 p-6">
            <div className="flex items-start gap-3">
              <span className="rounded-lg bg-primary/10 p-2.5 text-primary" aria-hidden="true">
                <KeyRound size={18} />
              </span>
              <div>
                <p className="font-bold text-on-surface">Hasło</p>
                <p className="mt-1 text-sm leading-relaxed text-on-surface-variant">
                  Po zmianie hasła zostaniesz wylogowany ze wszystkich urządzeń.
                </p>
              </div>
            </div>

            <Link
              to="/zmiana-hasla"
              className="flex w-full items-center justify-center rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-on-primary shadow-raised transition-all hover:bg-primary/90 active:scale-[0.98]"
            >
              Zmień hasło
            </Link>

            {user.role === 'TEACHER' && (
              // Nauczyciel nie ma w menu sekcji „Nauczyciele”, więc do własnego
              // profilu i dostępności tygodniowej wchodzi stąd.
              <Link
                to={`/nauczyciele/${user.id}`}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-outline/20 px-6 py-2.5 text-sm font-bold text-on-surface transition-colors hover:bg-surface-container-low hover:text-primary"
              >
                <GraduationCap size={16} aria-hidden="true" />
                Mój profil i dostępność
              </Link>
            )}

            <div className="rounded-lg bg-surface-container-low p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">
                Twoja rola
              </p>
              <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">
                {ROLE_DESCRIPTIONS[user.role]}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div>
      <dt className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-on-surface-variant">
        <span aria-hidden="true">{icon}</span>
        {label}
      </dt>
      <dd className="mt-1.5 break-words text-sm font-semibold text-on-surface">{value}</dd>
    </div>
  )
}
